if (!customElements.get('vl-pdp-specification-accordion')) {
  class VLPDPSpecificationAccordion extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.isInitialized = false;
      this.layoutObserver = null;
      this.layoutFrame = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.scheduleIndependentColumnLayout = this.scheduleIndependentColumnLayout.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.items = [...this.querySelectorAll('[data-vl-pdp-specification-item]')];
      this.halfGrid = this.querySelector('[data-vl-pdp-specification-half-grid]');
      this.halfItems = [
        ...this.querySelectorAll('.vl-pdp-specification-accordion__item--half'),
      ];
      if (this.items.length === 0) return;

      this.isInitialized = true;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.addEventListener('click', this.handleClick, { signal });
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });

      if ('ResizeObserver' in window && this.halfGrid && this.halfItems.length > 0) {
        this.layoutObserver = new ResizeObserver(this.scheduleIndependentColumnLayout);
        this.layoutObserver.observe(this.halfGrid);
        this.halfItems.forEach((item) => this.layoutObserver.observe(item));
      } else {
        window.addEventListener('resize', this.scheduleIndependentColumnLayout, { signal });
      }

      this.scheduleIndependentColumnLayout();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.layoutObserver?.disconnect();
      if (this.layoutFrame !== null) cancelAnimationFrame(this.layoutFrame);
      this.abortController = null;
      this.layoutObserver = null;
      this.layoutFrame = null;
      this.items = [];
      this.halfItems = [];
      this.halfGrid = null;
      this.isInitialized = false;
    }

    handleClick(event) {
      const button = event.target.closest('[data-vl-pdp-specification-button]');
      if (!button || !this.contains(button)) return;

      const item = button.closest('[data-vl-pdp-specification-item]');
      if (!item) return;

      this.setItemOpen(item, !item.hasAttribute('data-open'));
    }

    handleBlockSelect(event) {
      if (event.detail.sectionId !== this.dataset.sectionId) return;

      const item = this.items.find((candidate) => candidate.dataset.blockId === event.detail.blockId);
      if (!item) return;

      this.setItemOpen(item, true);
      item.scrollIntoView({ behavior: event.detail.load ? 'auto' : 'smooth', block: 'nearest' });
    }

    setItemOpen(item, shouldOpen) {
      if (shouldOpen && this.dataset.allowMultiple !== 'true') {
        this.items.forEach((candidate) => {
          if (candidate !== item) this.updateItem(candidate, false);
        });
      }

      this.updateItem(item, shouldOpen);
    }

    updateItem(item, shouldOpen) {
      const button = item.querySelector('[data-vl-pdp-specification-button]');
      const panel = item.querySelector('[data-vl-pdp-specification-panel]');

      item.toggleAttribute('data-open', shouldOpen);
      button?.setAttribute('aria-expanded', String(shouldOpen));
      panel?.setAttribute('aria-hidden', String(!shouldOpen));
    }

    scheduleIndependentColumnLayout() {
      if (this.layoutFrame !== null) return;

      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = null;
        this.updateIndependentColumnLayout();
      });
    }

    updateIndependentColumnLayout() {
      if (!this.halfGrid || this.halfItems.length === 0) return;

      if (!window.matchMedia('(min-width: 768px)').matches) {
        this.halfGrid.classList.remove('is-independent-columns');
        this.halfItems.forEach((item) => {
          item.style.removeProperty('--vl-pdp-specification-row-span');
        });
        return;
      }

      this.halfItems.forEach((item) => {
        const rowSpan = Math.max(1, Math.ceil(item.getBoundingClientRect().height));
        item.style.setProperty('--vl-pdp-specification-row-span', rowSpan);
      });

      this.halfGrid.classList.add('is-independent-columns');
    }
  }

  customElements.define('vl-pdp-specification-accordion', VLPDPSpecificationAccordion);
}
